import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Mail, ShieldCheck, AlertCircle } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/seo/SEO";
import { useMember } from "@/context/MemberAuthContext";
import { formatApiError } from "@/context/AuthContext";

const ERROR_COPY = {
  invalid: "That sign-in link is invalid or already used. Request a new one below.",
  expired: "That sign-in link expired. Request a new one below.",
};

const Login = () => {
  const { member, requestMagicLink } = useMember();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const urlError = searchParams.get("error");

  useEffect(() => {
    if (urlError && ERROR_COPY[urlError]) setError(ERROR_COPY[urlError]);
  }, [urlError]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await requestMagicLink(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  // Already signed in? Send them to /account.
  if (member && member.email) {
    return (
      <main
        data-testid="login-already-signed-in"
        className="min-h-screen bg-[#F7F7F4]"
      >
        <Header />
        <section className="max-w-xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <div className="overline">— You&rsquo;re already in</div>
          <h1 className="mt-5 font-sora text-[36px] md:text-[44px] font-bold text-[#111111] tracking-tight leading-tight">
            Signed in as<br />
            <span className="text-[#2563EB]">{member.email}</span>.
          </h1>
          <Link
            to="/account"
            data-testid="login-goto-account"
            className="mt-10 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg transition-colors"
          >
            Open my account
            <ArrowUpRight size={16} strokeWidth={2.25} />
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid="login-page" className="min-h-screen bg-[#F7F7F4] flex flex-col">
      <SEO
        title="Sign in"
        description="Sign in to your Vaymilo account using a one-time magic link sent to your email."
        path="/login"
        noindex
      />
      <Header />

      <section className="flex-1">
        <div className="max-w-xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="overline">— Sign in</div>
          <h1 className="mt-5 font-sora text-[36px] md:text-[48px] font-extrabold text-[#111111] tracking-tight leading-[1.05]">
            Welcome back.
          </h1>
          <p className="mt-5 font-manrope text-[16px] md:text-[17px] text-[#111111]/65 leading-relaxed">
            Enter your email and we&rsquo;ll send you a one-time sign-in link.
            No passwords to remember.
          </p>

          {sent ? (
            <div
              data-testid="login-sent-confirmation"
              className="mt-10 bg-white border border-[#E7E7E2] rounded-2xl p-8"
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#2563EB]/10 text-[#2563EB]">
                <Mail size={20} strokeWidth={2.25} />
              </div>
              <h2 className="mt-5 font-sora text-[22px] font-bold text-[#111111] tracking-tight">
                Check your inbox.
              </h2>
              <p className="mt-3 font-manrope text-[15px] text-[#111111]/70 leading-relaxed">
                We just sent a sign-in link to{" "}
                <span className="font-semibold text-[#111111]">{email}</span>.
                The link is valid for 15 minutes and works once.
              </p>
              <p className="mt-4 font-manrope text-[13px] text-[#111111]/50 leading-relaxed">
                Didn&rsquo;t arrive? Check your spam folder, or{" "}
                <button
                  type="button"
                  data-testid="login-try-again"
                  onClick={() => {
                    setSent(false);
                    setEmail("");
                  }}
                  className="text-[#2563EB] hover:underline"
                >
                  try a different address
                </button>
                .
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="login-form"
              className="mt-10 bg-white border border-[#E7E7E2] rounded-2xl p-7 md:p-8"
            >
              <label
                htmlFor="login-email"
                className="block font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/55"
              >
                Email
              </label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 w-full border border-[#E7E7E2] bg-[#F7F7F4] focus:bg-white focus:border-[#2563EB] outline-none rounded-lg px-4 py-3 font-manrope text-[15px] text-[#111111] transition-colors"
              />
              {error ? (
                <div
                  data-testid="login-error"
                  className="mt-4 flex items-start gap-2 font-manrope text-[13px] text-[#B91C1C]"
                >
                  <AlertCircle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              ) : null}

              <button
                type="submit"
                data-testid="login-submit-btn"
                disabled={submitting}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg transition-colors"
              >
                {submitting ? "Sending link…" : "Send sign-in link"}
                {!submitting && <ArrowUpRight size={16} strokeWidth={2.25} />}
              </button>

              <div className="mt-6 flex items-start gap-2 font-manrope text-[12.5px] text-[#111111]/55 leading-relaxed">
                <ShieldCheck size={13} className="mt-[2px] shrink-0 text-[#2563EB]" />
                <span>
                  Passwordless. We never store your password. The link in
                  the email is single-use and expires in 15 minutes.
                </span>
              </div>
            </form>
          )}

          <p className="mt-10 font-manrope text-[14px] text-[#111111]/65 text-center">
            New to Vaymilo?{" "}
            <Link
              to="/pricing"
              data-testid="login-link-pricing"
              className="text-[#111111] hover:text-[#2563EB] border-b border-[#E7E7E2] hover:border-[#2563EB] pb-0.5"
            >
              See membership options →
            </Link>
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Login;
